/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import {
  getUserBraindateCount,
  getUserResponseDelay,
} from '@e180/domain/lib/user/util';

import styles from './UserStats.style';

import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';
import messages from 'src/shared/domain/user/l10n/userL10n';

import type { User } from '@e180/domain/lib/user/type';

type Props = {
  user: User,
  // Self passed
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const UserStats = ({ user, intl }: Props): React.Node => {
  const braindatesCount = getUserBraindateCount(user);
  const responseDelay = getUserResponseDelay(user);

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const { braindateSingular, braindatePlural } = useEventBraindateName();

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const items = [];

  if (typeof braindatesCount === 'number') {
    const text = intl.formatMessage(messages.braindateCount, {
      braindateCount: braindatesCount,
      braindateSingular,
      braindatePlural,
    });

    items.push(
      <li className={classes.item} key={0}>
        {text}
      </li>,
    );
  }

  const replyMessages = {
    MINUTES: intl.formatMessage(messages.replyWithinMinutes),
    HOURS: intl.formatMessage(messages.replyWithinHours),
    DAYS: intl.formatMessage(messages.replyWithinDays),
  };

  if (responseDelay) {
    items.push(
      <li className={classNames(classes.item, classes.responseDelay)} key={1}>
        {replyMessages[responseDelay]}
      </li>,
    );
  }

  return <ul className={classes.root}>{items}</ul>;
};

export default compose(React.memo, injectIntl)(UserStats);
